import React, { useReducer, useCallback } from 'react';
import { TFileId, TLanguageIDs, IItemForGetShortInfo, IDefaultLanguage, ICreateCategory } from 'types';
import {
	createCategoryInitialState,
	CreateCategoryContext,
	TVisibleForm,
	TTCategoryType,
	TTypeOfModalWindow,
} from './createCategoryContext';
import { createCategoryReducer } from './createCategoryReducer';
import { CreateCategoryActionTypes } from './createCategoryActions';

const CreateCategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createCategoryReducer, createCategoryInitialState);

	const changeVisibleForm = useCallback((value: TVisibleForm) => {
		dispatch({
			type: CreateCategoryActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: CreateCategoryActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateCategoryActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				customers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateCategoryActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateCategoryActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setIDsRestaurant = useCallback((value: string[]) => {
		dispatch({
			type: CreateCategoryActionTypes.SET_IDS_RESTAURANT,
			payload: {
				IDsSelectedRestaurant: value,
			},
		});
	}, []);

	const changeNameOfCategory = useCallback((value: string) => {
		dispatch({
			type: CreateCategoryActionTypes.CHANGE_NAME_OF_CATEGORY,
			payload: {
				nameOfCategory: value,
			},
		});
	}, []);

	const setCategoryType = useCallback((value: TTCategoryType) => {
		dispatch({
			type: CreateCategoryActionTypes.SET_CATEGORY_TYPE,
			payload: {
				categoryType: value,
			},
		});
	}, []);

	const toggleIsRestricted = useCallback(() => {
		dispatch({
			type: CreateCategoryActionTypes.TOGGLE_IS_RESTRICTED,
		});
	}, []);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: CreateCategoryActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: CreateCategoryActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const setFetchedTranslations = useCallback(
		(value: TLanguageIDs[], defaultCustomerLanguage: Nullable<IDefaultLanguage>) => {
			const translations = value.map((id) => ({ languageId: id, name: '' }));
			dispatch({
				type: CreateCategoryActionTypes.SET_FETCHED_TRANSLATIONS,
				payload: {
					translations,
					defaultCustomerLanguage,
				},
			});
		},
		[],
	);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: CreateCategoryActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateCategoryActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: ICreateCategory | null) => {
		dispatch({
			type: CreateCategoryActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: CreateCategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: CreateCategoryActionTypes.RESET_FIELDS }), []);

	return (
		<CreateCategoryContext.Provider
			value={{
				changeVisibleForm,
				setFileId,
				setFetchedCustomers,
				setIDCustomer,
				setFetchedRestaurants,
				setIDsRestaurant,
				changeNameOfCategory,
				setCategoryType,
				toggleIsRestricted,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setFetchedTranslations,
				setTranslationTab,
				changeNameTranslation,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				visibleForm: state.visibleForm,
				fileId: state.fileId,
				fetchedCustomers: state.fetchedCustomers,
				IDSelectedCustomer: state.IDSelectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				nameOfCategory: state.nameOfCategory,
				categoryType: state.categoryType,
				isRestricted: state.isRestricted,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</CreateCategoryContext.Provider>
	);
};

export default CreateCategoryState;
